import styled from '@emotion/styled';
import { useMatches } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import type { MessageDescriptor } from '@lingui/core';
import type { ComponentType, ReactNode } from 'react';
import { Text } from 'components/text/Text';
import { useFeatureFlagWithLoading } from 'modules/accessControl/hooks/useFeatureFlag';
import { useOrgType } from 'modules/accessControl/hooks/useOrg';
import { UserDetails } from './UserDetails';
import { OldSiteLink } from './OldSiteLink';

interface RouteHandle {
  title?: MessageDescriptor;
  supplierTitle?: MessageDescriptor;
  header?: ComponentType;
  topbarBackground?: TopbarBackground;
}

/** The topbar's surface: `bg.secondary` (beige, default) or `bg.primary` (white). */
type TopbarBackground = 'primary' | 'secondary';

const DEFAULT_BACKGROUND: TopbarBackground = 'secondary';

/**
 * Every `RouteHandle` key is optional, so any object may carry them — the per-key
 * `.find()` predicates below do the real narrowing.
 */
function isRouteHandle(handle: unknown): handle is RouteHandle {
  return typeof handle === 'object' && handle !== null;
}

function usePageTitle(): string | undefined {
  const { i18n } = useLingui();
  const matches = useMatches();
  const isSupplier = useOrgType(2);
  const match = matches
    .slice()
    .reverse()
    .find(m => isRouteHandle(m.handle) && m.handle.title);
  const handle = isRouteHandle(match?.handle) ? match.handle : undefined;
  if (!handle?.title) return undefined;
  if (isSupplier && handle.supplierTitle) {
    return i18n._(handle.supplierTitle);
  }
  return i18n._(handle.title);
}

/** Deepest matched route wins, mirroring `usePageTitle`. */
function useTopbarHeader(): ComponentType | undefined {
  const matches = useMatches();
  const match = matches
    .slice()
    .reverse()
    .find(m => isRouteHandle(m.handle) && m.handle.header);
  return isRouteHandle(match?.handle) ? match.handle.header : undefined;
}

/** Deepest matched route wins, mirroring `usePageTitle`. */
function useTopbarBackground(): TopbarBackground {
  const matches = useMatches();
  const match = matches
    .slice()
    .reverse()
    .find(m => isRouteHandle(m.handle) && m.handle.topbarBackground);
  const handle = isRouteHandle(match?.handle) ? match.handle : undefined;
  return handle?.topbarBackground ?? DEFAULT_BACKGROUND;
}

export const Topbar = ({
  inviteActions,
  canShareInviteCode,
}: {
  inviteActions?: ReactNode;
  canShareInviteCode?: boolean;
}) => {
  const Header = useTopbarHeader();
  const title = usePageTitle();
  const background = useTopbarBackground();
  const { enabled: isBrandSupplierConnections, isLoading: isFeatureFlagLoading } =
    useFeatureFlagWithLoading('BrandSupplierConnections');

  return (
    <Container bgVariant={background}>
      <LeftBar>
        {Header ? (
          <Header />
        ) : (
          title && (
            <Text variant="headlineLg" weight="semiBold" noWrap>
              {title}
            </Text>
          )
        )}
      </LeftBar>
      <RightBar>
        <OldSiteLink />
        <UserDetails
          inviteActions={inviteActions}
          isBrandSupplierConnections={isBrandSupplierConnections}
          isFeatureFlagLoading={isFeatureFlagLoading}
          canShareInviteCode={canShareInviteCode}
        />
      </RightBar>
    </Container>
  );
};

const Container = styled.header<{ bgVariant: TopbarBackground }>`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  height: 88px;
  background-color: ${p => p.theme.palette.bg[p.bgVariant]};
  border-bottom: 0.5px solid ${p => p.theme.palette.border.primarySubtle};
  padding: 24px;
  align-items: center;
  gap: 24px;
`;

const RightBar = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
`;

const LeftBar = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;
